import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { ArrowLeft, Check, Loader2, Pencil, Sparkles, Image as ImageIcon, Layers, X } from 'lucide-react';
import EnhancedImagePreview from './EnhancedImagePreview';
import { getInspirationalDesignById, updateInspirationalDesign } from '../api/inspirationalDesign.api';
import { getKitsByInspirationalDesignId } from '../api/kit.api';
import {
  fetchRecommendedFabricsForGarment,
  getProductById,
  getProducts,
  getPublicFabricById,
} from '../api/product.api';
import { applyFabricToGarment } from '../api/realtime.api';
import { toThumbnailUrl } from '../utils/imageThumbnailUrl';
import { normalizeImageUrl } from '../utils/cdn';
import LazyImage from './LazyImage';
import FabricCatalogueView from './FabricCatalogueView';
import GarmentAnalysisEditorPanel, {
  FABRIC_ATTRIBUTE_LABELS,
  normalizeList,
} from './GarmentAnalysisEditorPanel';

function getGarmentImageSrc(design) {
  return design?.thumbnailUrl || design?.imageUrl || '';
}

function getKitOriginalImage(kit) {
  return (
    kit?.generatedImageUrl ||
    kit?.productDetails?.image ||
    kit?.generatedContent?.['Image Posts (Social Media)']?.[0]?.image ||
    ''
  );
}

function getKitImageSrc(kit) {
  const original = getKitOriginalImage(kit);
  return kit?.thumbnail || (original ? toThumbnailUrl(original) : '');
}

function handleImageFallback(event, fallbackSrc) {
  if (!fallbackSrc) return;
  const img = event.currentTarget;
  if (img.dataset.fallbackApplied === 'true') return;
  img.dataset.fallbackApplied = 'true';
  img.src = fallbackSrc;
}

function hasSuitableFabricAttributesForRecommendations(analysis) {
  const s = analysis?.suitableFabricAttributes;
  if (!s || typeof s !== 'object') return false;
  return Object.keys(FABRIC_ATTRIBUTE_LABELS).some((key) => normalizeList(s[key]).length > 0);
}

function canLoadRecommendedFabrics(analysis, design) {
  if (hasSuitableFabricAttributesForRecommendations(analysis)) return true;
  return Boolean((design?.imageUrl || design?.thumbnailUrl || '').trim());
}

function getFabricSourceBadge(source) {
  if (source === 'supplier') {
    return { label: 'Promoter', className: 'bg-amber-100 text-amber-900 ring-1 ring-amber-200/80' };
  }
  if (source === 'marketplace') {
    return { label: 'Marketplace', className: 'bg-sky-100 text-sky-900 ring-1 ring-sky-200/80' };
  }
  if (source === 'catalog') {
    return { label: 'Catalog', className: 'bg-violet-100 text-violet-900 ring-1 ring-violet-200/80' };
  }
  return { label: 'Yours', className: 'bg-indigo-100 text-indigo-900 ring-1 ring-indigo-200/80' };
}

function getProductImageUrlFromProduct(product = {}) {
  const image = product.image;
  if (typeof image === 'string' && image.trim()) return image.trim();
  const first = product.images?.[0];
  if (typeof first === 'string' && first.trim()) return first.trim();
  if (first && typeof first === 'object' && first.url) return String(first.url).trim();
  return '';
}

function mapProductToFabricCard(product) {
  const imageUrl = getProductImageUrlFromProduct(product);
  const fa = product.fabricAnalysis?.fabric_analysis || {};
  return {
    _id: product._id,
    name: product.name || 'Fabric',
    imageUrl,
    thumbnailUrl: imageUrl ? toThumbnailUrl(imageUrl) : '',
    fabricType: fa.fabric_type || '',
    weaveOrKnit: fa.weave_or_knit || '',
    gsmWeight: fa.gsm_weight || '',
    opacity: fa.opacity || '',
    stretch: fa.stretch || '',
    matchedAttributes: [],
    source: 'yours',
  };
}

async function fetchAllMyFabricProducts() {
  const all = [];
  let page = 1;
  let hasMore = true;
  while (hasMore && page <= 15) {
    const res = await getProducts(page, 100, '');
    const batch = Array.isArray(res?.products) ? res.products : [];
    all.push(...batch);
    hasMore = Boolean(res?.pagination?.hasNextPage) && batch.length > 0;
    page += 1;
  }
  return all;
}

function summaryToCatalogueFabric(summary) {
  const imageUrl = (summary?.imageUrl && String(summary.imageUrl).trim()) || '';
  return {
    _id: summary._id,
    name: summary.name || 'Fabric',
    image: imageUrl,
    images: imageUrl ? [imageUrl] : [],
    fabricAnalysis: {
      fabric_analysis: {
        fabric_type: summary.fabricType || '',
        weave_or_knit: summary.weaveOrKnit || '',
        gsm_weight: summary.gsmWeight || '',
        opacity: summary.opacity || '',
        stretch: summary.stretch || '',
      },
    },
  };
}

async function loadFabricForDetail(summary) {
  const id = String(summary._id);
  if (summary.source === 'marketplace') {
    const full = await getPublicFabricById(id);
    if (!full) throw new Error('Fabric not found');
    return full;
  }
  try {
    const full = await getProductById(id);
    if (full) return full;
  } catch {
    // fall through — promoter or public edge cases
  }
  const pub = await getPublicFabricById(id);
  if (!pub) throw new Error('Fabric not found or not available');
  return pub;
}

function MatchedFabricAttributes({ matchedAttributes }) {
  if (!Array.isArray(matchedAttributes) || matchedAttributes.length === 0) return null;

  const chips = matchedAttributes.flatMap((attr) =>
    (attr.values || []).map((value) => ({
      key: `${attr.key}-${value}`,
      label: attr.label,
      value,
    }))
  );
  if (chips.length === 0) return null;

  const visible = chips.slice(0, 4);
  const extra = chips.length - visible.length;

  return (
    <div className="mt-2 space-y-1">
      <p className="text-[9px] font-semibold uppercase tracking-wide text-emerald-700/90">Matched</p>
      <div className="flex flex-wrap gap-1">
        {visible.map((chip) => (
          <span
            key={chip.key}
            title={`${chip.label}: ${chip.value}`}
            className="inline-flex max-w-full items-center rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium leading-tight text-emerald-900 ring-1 ring-emerald-200/70"
          >
            <span className="truncate">
              {chip.label}: {chip.value}
            </span>
          </span>
        ))}
        {extra > 0 ? (
          <span
            className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600"
            title={chips
              .slice(4)
              .map((c) => `${c.label}: ${c.value}`)
              .join('\n')}
          >
            +{extra}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function FabricCardsCarousel({
  fabrics,
  design,
  applyingFabricId,
  fabricDetailLoadingId,
  showMatchedAttributes = true,
  onOpenFabricPreview,
  onUseFabric,
  scrollbarClass = '[&::-webkit-scrollbar-thumb]:bg-indigo-200/80',
}) {
  const fabricsScrollRef = useRef(null);

  return (
    <div
      ref={fabricsScrollRef}
      className={[
        'mt-4 w-full min-w-0 max-w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain',
        'touch-pan-x [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]',
        'pb-2 pt-0.5 sm:mt-5',
        '[&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full',
        scrollbarClass,
      ].join(' ')}
    >
      <div className="inline-flex max-w-none flex-nowrap items-stretch gap-3 pr-1 sm:gap-4 sm:pr-2">
        {fabrics.map((f) => {
          const original = (f.imageUrl && String(f.imageUrl).trim()) || '';
          const originalNorm = original ? normalizeImageUrl(original) : '';
          const explicitThumb = (f.thumbnailUrl && String(f.thumbnailUrl).trim()) || '';
          const derivedThumb = original ? toThumbnailUrl(original) : '';
          const thumbRaw = explicitThumb || derivedThumb;
          const primarySrc = thumbRaw ? normalizeImageUrl(thumbRaw) : '';
          const displaySrc = primarySrc || originalNorm;
          const imageFallbackSrc =
            originalNorm && displaySrc && displaySrc !== originalNorm ? originalNorm : null;
          const subtitle = [f.fabricType, f.weaveOrKnit].filter(Boolean).join(' · ') || 'Fabric';
          const sourceBadge = getFabricSourceBadge(f.source);
          const isApplying = applyingFabricId === String(f._id);
          const canUse = Boolean(design?.imageUrl && original) && !applyingFabricId;

          return (
            <article
              key={String(f._id)}
              className="flex w-[11rem] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-md shadow-slate-200/30 ring-1 ring-slate-100/80 sm:w-[11.5rem] sm:rounded-xl sm:shadow-sm"
            >
              <button
                type="button"
                onClick={() => onOpenFabricPreview(f)}
                disabled={fabricDetailLoadingId === String(f._id)}
                className="group relative h-[9.25rem] w-full shrink-0 overflow-hidden bg-slate-100 text-left sm:h-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1"
                aria-label={`View ${f.name || 'fabric'} details`}
              >
                {displaySrc ? (
                  <LazyImage
                    src={displaySrc}
                    fallbackSrc={imageFallbackSrc}
                    alt={f.name || 'Fabric'}
                    scrollRootRef={fabricsScrollRef}
                    allowFallback={Boolean(imageFallbackSrc)}
                    className="relative h-full w-full transition-transform duration-200 group-hover:scale-[1.02] group-active:scale-[0.99]"
                    imgClassName="h-full w-full object-cover object-center"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center px-2 text-center text-[11px] text-slate-400">
                    No image
                  </div>
                )}
                <span
                  className={[
                    'pointer-events-none absolute left-2 top-2 z-20 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide shadow-sm',
                    sourceBadge.className,
                  ].join(' ')}
                >
                  {sourceBadge.label}
                </span>
                {fabricDetailLoadingId === String(f._id) ? (
                  <span className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-500" aria-hidden />
                  </span>
                ) : (
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/50 to-transparent px-2 py-1.5 text-[9px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 sm:opacity-100">
                    View details
                  </span>
                )}
              </button>
              <div className="flex min-h-0 min-w-0 flex-1 flex-col p-3">
                <div className="min-h-0 flex-1 space-y-1">
                  <h3
                    className="line-clamp-2 break-words text-left text-xs font-semibold leading-snug text-slate-900 [overflow-wrap:anywhere]"
                    title={f.name}
                  >
                    {f.name}
                  </h3>
                  <p className="line-clamp-2 text-left text-[10px] leading-snug text-slate-500">{subtitle}</p>
                  {showMatchedAttributes ? (
                    <MatchedFabricAttributes matchedAttributes={f.matchedAttributes} />
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => onUseFabric(f)}
                  disabled={!canUse}
                  className={[
                    'mt-auto flex min-h-[44px] w-full shrink-0 items-center justify-center gap-1.5 rounded-xl border px-2 py-2 text-[11px] font-semibold transition-colors sm:min-h-0 sm:rounded-lg sm:py-2',
                    canUse
                      ? 'border-fuchsia-200 bg-gradient-to-r from-fuchsia-50 to-purple-50 text-fuchsia-900 active:bg-fuchsia-100 sm:hover:from-fuchsia-100 sm:hover:to-purple-100'
                      : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400',
                  ].join(' ')}
                >
                  {isApplying ? (
                    <Loader2 size={12} className="shrink-0 animate-spin" aria-hidden />
                  ) : (
                    <Sparkles size={12} className="shrink-0 opacity-80" aria-hidden />
                  )}
                  <span className="leading-tight">{isApplying ? 'Generating…' : 'Use this fabric'}</span>
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

function FabricSectionShell({
  title,
  description,
  icon: Icon = Layers,
  borderClass = 'border-indigo-100/90',
  headerExtra = null,
  loading,
  loadingLabel = 'Loading…',
  error,
  emptyMessage,
  children,
}) {
  return (
    <div
      className={`mt-6 min-w-0 max-w-full rounded-2xl border ${borderClass} bg-white p-4 shadow-sm sm:mt-8 sm:rounded-xl sm:p-6 max-md:shadow-md`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">{title}</h2>
          <p className="mt-1.5 text-xs leading-relaxed text-slate-600 sm:mt-1 sm:text-sm sm:leading-normal">
            {description}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <Icon className="h-7 w-7 text-indigo-300 sm:h-8 sm:w-8" aria-hidden />
          {headerExtra}
        </div>
      </div>
      {loading ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-indigo-500" />
          {loadingLabel}
        </div>
      ) : error ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          {error}
        </div>
      ) : emptyMessage ? (
        <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/90 px-3 py-6 text-center text-xs leading-relaxed text-slate-600 sm:px-4 sm:text-sm">
          {emptyMessage}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function GarmentFabricSections({
  designId,
  design,
  analysis,
  analysisRefreshKey = 0,
  showNotification,
  onKitSaved,
  applyingFabricId,
  setApplyingFabricId,
  setActiveView,
  setProductDetails,
}) {
  const [recommendedFabrics, setRecommendedFabrics] = useState([]);
  const [myFabrics, setMyFabrics] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [loadingMyFabrics, setLoadingMyFabrics] = useState(false);
  const [recommendedError, setRecommendedError] = useState(null);
  const [myFabricsError, setMyFabricsError] = useState(null);
  const [catalogueFabric, setCatalogueFabric] = useState(null);
  const [catalogueFabricSource, setCatalogueFabricSource] = useState(null);
  const [fabricDetailLoadingId, setFabricDetailLoadingId] = useState(null);
  const handleOpenFabricPreview = useCallback(
    async (summary) => {
      const id = String(summary._id);
      if (fabricDetailLoadingId === id) return;
      setFabricDetailLoadingId(id);
      setCatalogueFabricSource(summary.source || null);
      setCatalogueFabric(summaryToCatalogueFabric(summary));
      try {
        const full = await loadFabricForDetail(summary);
        setCatalogueFabric(full);
      } catch (e) {
        console.error(e);
        setCatalogueFabric(null);
        setCatalogueFabricSource(null);
        showNotification?.(e?.response?.data?.message || e?.message || 'Could not open fabric details', 'error');
      } finally {
        setFabricDetailLoadingId(null);
      }
    },
    [fabricDetailLoadingId, showNotification]
  );

  const handleUseFabric = async (fabric) => {
    const garmentUrl = (design?.imageUrl && String(design.imageUrl).trim()) || '';
    const fabricUrl = (fabric?.imageUrl && String(fabric.imageUrl).trim()) || '';
    if (!garmentUrl || !fabricUrl) {
      showNotification?.('Garment and fabric images are required to generate a concept.', 'error');
      return;
    }
    if (applyingFabricId) return;

    const fabricId = String(fabric._id);
    setApplyingFabricId(fabricId);
    try {
      const fabricName = fabric.name || 'Fabric';
      const garmentName = design?.productName || 'Garment';
      const result = await applyFabricToGarment({
        garmentUrl,
        fabricUrl,
        productCategory: analysis?.productCategory || 'women-clothing',
        productType: analysis?.productType || 'other',
        saveToKit: true,
        sourceProductId: fabricId,
        sourceInspirationalDesignId: designId,
        fabricName,
        productName: fabricName,
        kitName: `${fabricName} · ${garmentName}`
      });
      if (!result?.success || !result?.imageUrl) {
        throw new Error(result?.message || 'Failed to generate concept');
      }
      if (result.kit) {
        onKitSaved?.(result.kit);
        showNotification?.('Concept generated and saved to your kits.', 'success');
      } else {
        showNotification?.('Concept generated successfully.', 'success');
      }
    } catch (e) {
      console.error(e);
      const message = e?.response?.data?.message || e?.message || 'Failed to apply fabric';
      showNotification?.(message, 'error');
    } finally {
      setApplyingFabricId(null);
    }
  };

  const canRecommend = canLoadRecommendedFabrics(analysis, design);

  useEffect(() => {
    if (!designId) return;
    setMyFabrics([]);
    setMyFabricsError(null);
    let cancelled = false;
    (async () => {
      setLoadingMyFabrics(true);
      try {
        const products = await fetchAllMyFabricProducts();
        if (!cancelled) setMyFabrics(products.map(mapProductToFabricCard));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setMyFabrics([]);
          setMyFabricsError(e?.response?.data?.message || e?.message || 'Could not load your fabrics');
        }
      } finally {
        if (!cancelled) setLoadingMyFabrics(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [designId]);

  useEffect(() => {
    if (!designId || !canRecommend) {
      setRecommendedFabrics([]);
      setRecommendedError(null);
      setLoadingRecommended(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingRecommended(true);
      setRecommendedError(null);
      try {
        const garmentImageUrl = (design?.imageUrl && String(design.imageUrl).trim()) || '';
        const data = await fetchRecommendedFabricsForGarment({
          inspirationalDesignId: designId,
          garmentImageUrl: garmentImageUrl || undefined,
        });
        if (!cancelled) setRecommendedFabrics(Array.isArray(data.fabrics) ? data.fabrics : []);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setRecommendedFabrics([]);
          setRecommendedError(e?.response?.data?.message || e?.message || 'Could not load recommendations');
        }
      } finally {
        if (!cancelled) setLoadingRecommended(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [designId, analysis, design?.imageUrl, analysisRefreshKey, canRecommend]);

  if (!designId) {
    return null;
  }

  const recommendedDescription = (
    <>
      <span className="md:hidden">
        My Fabrics, promoters’ fabrics, and marketplace — filtered by fabric type, ranked by weave, weight, stretch,
        opacity, fabric visual, and garment image similarity.
      </span>
      <span className="hidden md:inline">
        Searched My Fabrics, your promoters’ public fabrics, and the public marketplace (entire catalog for admins).
        Matches recommended fabric types, then ranks by weave, weight, stretch, opacity, fabric visual, and visual
        similarity to this garment image (fabric visual matches are boosted when image search runs).
      </span>
    </>
  );

  const allFabricsDescription = (
    <>
      <span className="md:hidden">Every fabric in your My Fabrics library.</span>
      <span className="hidden md:inline">
        Browse your complete My Fabrics library. Preview any swatch or apply it to this garment.
      </span>
    </>
  );

  return (
    <>
      {canRecommend ? (
        <FabricSectionShell
          title="Recommended fabrics"
          description={recommendedDescription}
          loading={loadingRecommended}
          loadingLabel="Finding fabrics…"
          error={recommendedError}
          emptyMessage={
            recommendedFabrics.length === 0 && !loadingRecommended && !recommendedError
              ? 'No close matches yet. Add fabrics with AI analysis in My Fabrics, follow a fabric promoter, or browse the public fabric marketplace for more options.'
              : null
          }
        >
          {recommendedFabrics.length > 0 ? (
            <FabricCardsCarousel
              fabrics={recommendedFabrics}
              design={design}
              applyingFabricId={applyingFabricId}
              fabricDetailLoadingId={fabricDetailLoadingId}
              showMatchedAttributes
              onOpenFabricPreview={handleOpenFabricPreview}
              onUseFabric={handleUseFabric}
            />
          ) : null}
        </FabricSectionShell>
      ) : null}

      <FabricSectionShell
        title="All fabrics"
        description={allFabricsDescription}
        borderClass="border-slate-200/90"
        loading={loadingMyFabrics}
        loadingLabel="Loading your fabrics…"
        error={myFabricsError}
        emptyMessage={
          myFabrics.length === 0 && !loadingMyFabrics && !myFabricsError
            ? 'No fabrics in My Fabrics yet. Add fabrics from the My Fabrics page to use them here.'
            : null
        }
        headerExtra={
          setActiveView ? (
            <button
              type="button"
              onClick={() => setActiveView('myproducts')}
              className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
            >
              Open My Fabrics →
            </button>
          ) : null
        }
      >
        {myFabrics.length > 0 ? (
          <FabricCardsCarousel
            fabrics={myFabrics}
            design={design}
            applyingFabricId={applyingFabricId}
            fabricDetailLoadingId={fabricDetailLoadingId}
            showMatchedAttributes={false}
            onOpenFabricPreview={handleOpenFabricPreview}
            onUseFabric={handleUseFabric}
            scrollbarClass="[&::-webkit-scrollbar-thumb]:bg-slate-300/80"
          />
        ) : null}
      </FabricSectionShell>

    {catalogueFabric ? (
      <FabricCatalogueView
        fabric={catalogueFabric}
        onClose={() => {
          setCatalogueFabric(null);
          setCatalogueFabricSource(null);
        }}
        showNotification={showNotification}
        allowShadeCardUpload={catalogueFabricSource === 'yours'}
        setProductDetails={setProductDetails}
        setActiveView={setActiveView}
      />
    ) : null}
    </>
  );
}

const GarmentDesignDetailView = ({
  designId,
  onBack,
  showNotification,
  setActiveView,
  setProductDetails,
  onDesignUpdated,
}) => {
  const [design, setDesign] = useState(null);
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kitsLoadError, setKitsLoadError] = useState(null);
  const [applyingFabricId, setApplyingFabricId] = useState(null);
  const [analysisRefreshKey, setAnalysisRefreshKey] = useState(0);
  const [kitPreviewOpen, setKitPreviewOpen] = useState(false);
  const [kitPreviewIndex, setKitPreviewIndex] = useState(0);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);

  const kitPreviewItems = useMemo(
    () =>
      kits
        .map((k) => ({
          id: String(k._id),
          url: getKitOriginalImage(k),
          title: k.name || 'Kit',
        }))
        .filter((item) => item.url),
    [kits]
  );

  const load = useCallback(async () => {
    if (!designId) return;
    setLoading(true);
    setError(null);
    setKitsLoadError(null);
    setKits([]);
    try {
      const d = await getInspirationalDesignById(designId);
      setDesign(d);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e?.message || 'Failed to load garment');
      setDesign(null);
      setLoading(false);
      return;
    }

    try {
      const k = await getKitsByInspirationalDesignId(designId);
      setKits(Array.isArray(k) ? k : []);
    } catch (e) {
      console.error('Kits by garment:', e);
      setKits([]);
      setKitsLoadError(e?.response?.data?.message || e?.message || 'Could not load linked kits');
    } finally {
      setLoading(false);
    }
  }, [designId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.userId);
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    }
    setIsAdmin(localStorage.getItem('role')?.toLowerCase() === 'admin');
  }, []);

  useEffect(() => {
    setApplyingFabricId(null);
    setIsEditingName(false);
    setNameDraft('');
  }, [designId]);

  const isOwner = useMemo(() => {
    if (!currentUserId || !design?.user) return false;
    const designUserId = typeof design.user === 'object' ? design.user._id : design.user;
    return (
      String(designUserId) === String(currentUserId) ||
      designUserId?.toString() === currentUserId?.toString()
    );
  }, [currentUserId, design?.user]);

  const canEditName = isOwner || isAdmin;

  const startEditingName = () => {
    if (!canEditName || !design) return;
    setNameDraft(design.productName || '');
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setNameDraft('');
  };

  const saveGarmentName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      showNotification?.('Garment name cannot be empty', 'error');
      return;
    }
    if (!designId || trimmed === (design?.productName || '').trim()) {
      cancelEditingName();
      return;
    }
    setSavingName(true);
    try {
      const updated = await updateInspirationalDesign(designId, { productName: trimmed });
      setDesign((prev) => (prev ? { ...prev, ...updated, productName: updated?.productName ?? trimmed } : prev));
      onDesignUpdated?.(updated);
      setIsEditingName(false);
      setNameDraft('');
      showNotification?.('Garment name updated', 'success');
    } catch (e) {
      console.error(e);
      showNotification?.(e?.response?.data?.message || e?.message || 'Could not update garment name', 'error');
    } finally {
      setSavingName(false);
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveGarmentName();
    } else if (e.key === 'Escape') {
      cancelEditingName();
    }
  };

  const openKitImagePreview = (kit) => {
    const url = getKitOriginalImage(kit);
    if (!url) return;
    const index = kitPreviewItems.findIndex((item) => item.id === String(kit._id));
    setKitPreviewIndex(index >= 0 ? index : 0);
    setKitPreviewOpen(true);
  };

  const closeKitImagePreview = () => {
    setKitPreviewOpen(false);
  };

  return (
    <div className="min-h-0 min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto bg-gradient-to-b from-purple-50/60 via-white to-slate-50/40 pb-[max(5rem,calc(2.5rem+env(safe-area-inset-bottom)))]">
      <div className="mx-auto min-w-0 max-w-5xl px-3 py-3 sm:px-6 sm:py-4">
        <div className="max-md:sticky max-md:top-0 max-md:z-10 max-md:mb-3 max-md:border-b max-md:border-slate-200/50 max-md:bg-white/80 max-md:py-2 max-md:backdrop-blur-md sm:static sm:mb-4 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-slate-200/90 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition active:scale-[0.99] sm:min-h-0 sm:w-auto sm:justify-start sm:rounded-lg sm:px-3 sm:py-2 sm:font-medium sm:hover:bg-slate-50"
          >
            <ArrowLeft size={18} className="shrink-0 text-purple-600" />
            Back to garments
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-600 sm:py-24">
            <Loader2 className="h-10 w-10 shrink-0 animate-spin text-purple-500" />
            <p className="mt-3 text-sm font-medium">Loading garment…</p>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200/80 bg-red-50/90 p-5 text-center text-sm text-red-800 sm:rounded-xl sm:p-6">
            {error}
          </div>
        ) : design ? (
          <>
            <div className="overflow-hidden rounded-2xl border border-purple-100/80 bg-white shadow-md shadow-purple-900/5 sm:rounded-2xl sm:shadow-sm">
              <div className="grid gap-0 md:grid-cols-2">
                <div className="relative aspect-[4/5] bg-gradient-to-br from-slate-100 to-violet-50/30 md:aspect-square md:min-h-[320px] md:from-slate-100 md:to-slate-100">
                  <img
                    src={getGarmentImageSrc(design)}
                    alt={design.productName}
                    className="h-full w-full object-contain p-2"
                    loading="eager"
                    onError={(e) => handleImageFallback(e, design.imageUrl)}
                  />
                </div>
                <div className="flex flex-col justify-center border-t border-purple-100/40 p-5 sm:p-8 md:border-t-0 md:border-none">
                  {isEditingName ? (
                    <div className="space-y-3">
                      <label htmlFor="garment-name-input" className="sr-only">
                        Garment name
                      </label>
                      <input
                        id="garment-name-input"
                        type="text"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onKeyDown={handleNameKeyDown}
                        disabled={savingName}
                        maxLength={200}
                        autoFocus
                        className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2.5 text-lg font-bold text-slate-900 shadow-sm outline-none ring-purple-400/40 transition focus:border-purple-400 focus:ring-2 sm:text-xl md:text-2xl"
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={saveGarmentName}
                          disabled={savingName || !nameDraft.trim()}
                          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {savingName ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Check size={16} />
                          )}
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditingName}
                          disabled={savingName}
                          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          <X size={16} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <h1 className="min-w-0 flex-1 text-xl font-bold leading-tight tracking-tight text-slate-900 sm:text-2xl md:text-3xl">
                        {design.productName}
                      </h1>
                      {canEditName ? (
                        <button
                          type="button"
                          onClick={startEditingName}
                          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-purple-200 hover:bg-purple-50 hover:text-purple-800"
                          title="Edit garment name"
                        >
                          <Pencil size={14} />
                          <span className="hidden sm:inline">Edit</span>
                        </button>
                      ) : null}
                    </div>
                  )}
                  <p className="mt-2.5 flex items-center gap-2 text-xs text-slate-500 sm:mt-2 sm:text-sm">
                    <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-purple-400" aria-hidden />
                    Added {design.createdAt ? new Date(design.createdAt).toLocaleString() : '—'}
                  </p>
                  {design.isPublic ? (
                    <span className="mt-3 inline-flex w-fit items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-900 ring-1 ring-emerald-200/60">
                      Public on storefront
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mt-6 sm:mt-8">
              <GarmentAnalysisEditorPanel
                analysis={design.garmentAnalysis}
                designId={designId}
                designImageUrl={design.imageUrl}
                showNotification={showNotification}
                onSaved={(updated) => {
                  setDesign((prev) =>
                    prev ? { ...prev, garmentAnalysis: updated?.garmentAnalysis ?? prev.garmentAnalysis } : prev
                  );
                  setAnalysisRefreshKey((k) => k + 1);
                }}
              />
            </div>

            <GarmentFabricSections
              designId={designId}
              design={design}
              analysis={design.garmentAnalysis}
              analysisRefreshKey={analysisRefreshKey}
              showNotification={showNotification}
              setActiveView={setActiveView}
              setProductDetails={setProductDetails}
              onKitSaved={(kit) => {
                if (!kit?._id) return;
                const kitId = String(kit._id);
                setKits((prev) => {
                  if (prev.some((k) => String(k._id) === kitId)) return prev;
                  return [kit, ...prev];
                });
              }}
              applyingFabricId={applyingFabricId}
              setApplyingFabricId={setApplyingFabricId}
            />

            <section className="mt-8 scroll-mt-20 overflow-visible pb-8 sm:mt-10 sm:pb-12">
              <div className="flex items-center gap-2">
                <span className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500 sm:h-7" aria-hidden />
                <h2 className="text-base font-bold leading-snug tracking-tight text-slate-900 sm:text-lg">AI kits from this garment</h2>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-slate-600 sm:text-sm">
                <span className="md:hidden">
                  Kits from Generate or from applying a recommended fabric to this garment.
                </span>
                <span className="hidden md:inline">
                  Kits created in Generate with this body cut, or by using a recommended fabric above.
                </span>
              </p>
              {applyingFabricId ? (
                <div className="mt-4 flex items-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50/80 px-3 py-3 text-sm text-indigo-900">
                  <Loader2 className="h-5 w-5 shrink-0 animate-spin text-indigo-500" />
                  Applying fabric and generating kit…
                </div>
              ) : null}
              {kitsLoadError ? (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-900 sm:p-4">
                  {kitsLoadError}
                </div>
              ) : null}
              {kits.length === 0 && !kitsLoadError && !applyingFabricId ? (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/90 p-6 text-center text-xs leading-relaxed text-slate-600 sm:rounded-xl sm:p-8 sm:text-sm">
                  <ImageIcon className="mx-auto mb-2 h-9 w-9 text-slate-300 sm:h-10 sm:w-10" />
                  No kits linked yet. Use a recommended fabric above or generate a kit with this design.
                </div>
              ) : kits.length > 0 ? (
                <ul className="mt-4 grid grid-cols-2 items-start gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4">
                  {kits.map((kit) => {
                    const originalSrc = getKitOriginalImage(kit);
                    const imageSrc = getKitImageSrc(kit);
                    const kitName = kit.name || 'Kit';
                    return (
                      <li key={kit._id} className="min-w-0">
                        <button
                          type="button"
                          onClick={() => openKitImagePreview(kit)}
                          disabled={!getKitOriginalImage(kit)}
                          className={[
                            'flex w-full min-w-0 flex-col rounded-xl border border-slate-200/90 bg-white text-left shadow-sm ring-1 ring-slate-100/60 transition sm:hover:border-purple-300 sm:hover:shadow-md',
                            getKitOriginalImage(kit) ? 'active:scale-[0.98] cursor-pointer' : 'cursor-default opacity-90'
                          ].join(' ')}
                        >
                          <div className="aspect-[3/4] overflow-hidden rounded-t-xl bg-slate-100">
                            {imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={kitName}
                                className="h-full w-full object-contain"
                                loading="lazy"
                                onError={(e) => handleImageFallback(e, originalSrc)}
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center text-[10px] text-slate-400 sm:text-xs">
                                No preview
                              </div>
                            )}
                          </div>
                          <div className="rounded-b-xl bg-white px-2.5 py-2.5 pb-3">
                            <p
                              className="line-clamp-2 break-words text-left text-[11px] font-semibold leading-snug text-slate-800 [overflow-wrap:anywhere] sm:text-xs"
                              title={kitName}
                            >
                              {kitName}
                            </p>
                            <p className="mt-1 text-[10px] text-slate-500">
                              {kit.createdAt ? new Date(kit.createdAt).toLocaleDateString() : ''}
                            </p>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </section>
          </>
        ) : null}
      </div>

      <EnhancedImagePreview
        isOpen={kitPreviewOpen && kitPreviewItems.length > 0}
        onClose={closeKitImagePreview}
        imageUrl={kitPreviewItems[kitPreviewIndex]?.url || ''}
        title={kitPreviewItems[kitPreviewIndex]?.title || 'Generated image'}
        images={kitPreviewItems.map((item) => item.url)}
        currentIndex={kitPreviewIndex}
        onImageChange={setKitPreviewIndex}
        showMetadata
        allowDownload
        allowShare={false}
      />
    </div>
  );
};

export default GarmentDesignDetailView;
